// Outils communs de l'interface du module plateforme.
import { useState, type ReactNode } from "react";
import { ApiError } from "@core/api";
import { Badge, SimBadge } from "@core/ui";

/** Mode d'un service tiers : toujours visible, jamais présenté comme réel s'il est simulé. */
export function Mode({ mode }: { mode?: string | null }) {
  if (mode === "production") return <Badge tone="succes">Service réel</Badge>;
  if (mode === "sandbox") return <SimBadge mode="sandbox" />;
  return <SimBadge mode="simulateur" />;
}

/** Exécute une action serveur avec état « en cours » et erreur affichable. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e);
      return undefined;
    } finally {
      setBusy(false);
    }
  }
  return { run, busy, error, setError };
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Erreur inattendue.";
}

const microFmt = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 4 });

/** Microcentimes (1 € = 100 000 000) en euros, jusqu'à 4 décimales. */
export function microEuros(micro: number | null | undefined): string {
  if (micro === null || micro === undefined) return "—";
  return microFmt.format(micro / 1e8);
}

/** Saisie « 0,02 » ou « 1,5 » en microcentimes ; null si invalide. */
export function parseMicroEuros(input: string): number | null {
  const cleaned = input.replace(/[\s  €]/g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,6})?$/.test(cleaned)) return null;
  const [int, dec = ""] = cleaned.split(".");
  return parseInt(int, 10) * 1e8 + parseInt((dec + "000000").slice(0, 6), 10) * 100;
}

/** Jauge « utilisé / limite » : le texte porte l'information, la barre l'illustre. */
export function Gauge({ label, used, limit, note }: { label: string; used: number; limit: number | null; note?: ReactNode }) {
  const over = limit !== null && used > limit;
  const pct = limit ? Math.min(100, Math.round((used / Math.max(limit, 1)) * 100)) : 0;
  return (
    <div className={`plateforme-jauge ${over ? "plateforme-jauge-depasse" : ""}`}>
      <div className="plateforme-jauge-tete">
        <span>{label}</span>
        <span className="num">
          {used}
          {limit !== null ? ` / ${limit}` : " (sans limite)"}
          {over ? " · limite dépassée" : ""}
        </span>
      </div>
      {limit !== null ? (
        <div className="plateforme-jauge-piste" aria-hidden="true">
          <div className="plateforme-jauge-valeur" style={{ width: `${pct}%` }} />
        </div>
      ) : null}
      {note ? <span className="muted small">{note}</span> : null}
    </div>
  );
}

export const PLAN_CADENCE: Record<string, string> = {
  campagne: "par campagne",
  mois: "par mois",
  an: "par an",
  gratuit: "gratuit",
};

export const LEAD_STATUS: Record<string, { label: string; tone: "neutre" | "encre" | "succes" | "attention" | "erreur" }> = {
  autorise: { label: "Accord donné, à transmettre", tone: "encre" },
  transmis: { label: "Transmise au partenaire", tone: "encre" },
  recu: { label: "Reçue par le partenaire", tone: "encre" },
  contacte: { label: "Contact pris", tone: "encre" },
  devis_propose: { label: "Devis proposé", tone: "encre" },
  souscrit: { label: "Souscription confirmée par le partenaire", tone: "succes" },
  sans_suite: { label: "Sans suite", tone: "neutre" },
  retire: { label: "Retirée", tone: "neutre" },
  echec_transmission: { label: "Échec de transmission, à reprendre", tone: "erreur" },
};

export const PRODUCT_LABEL: Record<string, string> = {
  gli: "Loyers impayés (GLI)",
  pno: "Propriétaire non occupant (PNO)",
};

export const LEGAL_BASIS: Record<string, string> = {
  mesures_precontractuelles: "Mesures précontractuelles",
  contrat: "Exécution du contrat",
  obligation_legale: "Obligation légale",
  interet_legitime: "Intérêt légitime",
  consentement: "Consentement",
};
